import React, { useEffect, useRef, useState } from "react";
import { Save, Plus, Trash2, Radio as RadioIcon, Mic2, CalendarRange, Share2, Check, ExternalLink, Upload, Loader2, Copy, DollarSign } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import HelpHint from "@/components/HelpHint";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function Station() {
  const [station, setStation] = useState(null);
  const [shows, setShows] = useState([]);
  const [djs, setDjs] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [s, sh, d] = await Promise.all([
      api.get("/stations/me"),
      api.get("/shows"),
      api.get("/djs"),
    ]);
    setStation(s.data);
    setShows(sh.data);
    setDjs(d.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!station) return;
    setSaving(true);
    try {
      await api.patch(`/stations/${station.station_id}`, station);
      toast.success("Station saved");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const [logoUploading, setLogoUploading] = useState(false);
  const logoRef = useRef(null);
  const uploadLogo = async (file) => {
    if (!file || !station) return;
    setLogoUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post(`/stations/${station.station_id}/logo`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setStation((st) => ({ ...st, logo_url: res.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setLogoUploading(false); }
  };

  if (!station) {
    return <div className="p-10 text-ink-muted">Create your station first from the Control Room.</div>;
  }

  const upd = (k, v) => setStation({ ...station, [k]: v });

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="station-page">
      <PageHeader
        eyebrow="// STATION"
        title={station.name}
        description="Manage your station profile, streams, shows, and DJs."
        action={
          <button onClick={save} disabled={saving} data-testid="station-save-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 font-semibold text-black amber-glow disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}
          </button>
        }
      />

      <Tabs defaultValue="profile">
        <TabsList className="bg-bg-surface border border-line p-1 rounded-md mb-6 h-auto flex-wrap justify-start">
          <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Profile</TabsTrigger>
          <TabsTrigger value="streams" data-testid="tab-streams" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Streams</TabsTrigger>
          <TabsTrigger value="social" data-testid="tab-social" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Social</TabsTrigger>
          <TabsTrigger value="sponsorships" data-testid="tab-sponsorships" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Sponsorships</TabsTrigger>
          <TabsTrigger value="shows" data-testid="tab-shows" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">Shows</TabsTrigger>
          <TabsTrigger value="djs" data-testid="tab-djs" className="data-[state=active]:bg-brand-amber data-[state=active]:text-black">DJs</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          {station.founding_cohort_number && (
            <div className="mb-4 flex items-center justify-between rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4" data-testid="founding-badge-setting">
              <div>
                <div className="text-sm font-semibold text-amber-200">
                  ★ Founding Tester #{station.founding_cohort_number}
                </div>
                <div className="text-xs text-ink-secondary mt-0.5">
                  Show the Founding Tester badge on your public station page. Keep it off to stay private — your discount is unaffected either way.
                </div>
              </div>
              <Switch
                checked={!!station.founding_badge_public}
                onCheckedChange={(v) => upd("founding_badge_public", v)}
                data-testid="founding-badge-toggle"
              />
            </div>
          )}
          <div className="rounded-xl border border-line bg-bg-surface p-6 grid md:grid-cols-2 gap-4" data-testid="profile-tab">
            <Field label="Station name" value={station.name} onChange={(v) => upd("name", v)} testid="field-name" />
            <Field label="Genre" value={station.genre} onChange={(v) => upd("genre", v)} testid="field-genre" />
            <Field label="Tagline" value={station.tagline} onChange={(v) => upd("tagline", v)} testid="field-tagline" />
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Logo</Label>
              <div className="flex items-stretch gap-2">
                <Input value={station.logo_url || ""} onChange={(e) => upd("logo_url", e.target.value)}
                       placeholder="https://… or upload below"
                       className="bg-bg border-line flex-1" data-testid="field-logo" />
                <input ref={logoRef} type="file" accept="image/*" className="hidden"
                       onChange={(e) => uploadLogo(e.target.files?.[0])}
                       data-testid="logo-upload-input" />
                <button type="button" onClick={() => logoRef.current?.click()} disabled={logoUploading}
                        data-testid="logo-upload-btn"
                        className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-3 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition">
                  {logoUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {logoUploading ? "Uploading…" : "Upload image"}
                </button>
              </div>
              {station.logo_url && (
                <div className="mt-2 flex items-center gap-3" data-testid="logo-preview">
                  <img src={station.logo_url} alt="Station logo"
                       className="h-14 w-14 rounded-md border border-line bg-bg-elevated object-cover" />
                  <span className="text-[10px] font-mono text-ink-muted">Preview · PNG/JPG/WEBP · max 5 MB</span>
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Description</Label>
              <Textarea value={station.description || ""} onChange={(e) => upd("description", e.target.value)} className="mt-1 bg-bg border-line" rows={4} data-testid="field-description" />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="streams">
          <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-5" data-testid="streams-tab">
            <Note>
              <RadioIcon className="h-4 w-4" /> Drop in every stream &amp; directory URL your station uses. We&apos;ll surface them across share pages, podcast feeds, and player widgets.
            </Note>

            <div>
              <SectionLabel>Encoders &amp; servers</SectionLabel>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Shoutcast URL" value={station.shoutcast_url} onChange={(v) => upd("shoutcast_url", v)} mono testid="field-shoutcast" />
                <Field label="Icecast URL" value={station.icecast_url} onChange={(v) => upd("icecast_url", v)} mono testid="field-icecast" />
                <Field label="SAM Broadcaster URL" value={station.sam_broadcaster_url} onChange={(v) => upd("sam_broadcaster_url", v)} mono testid="field-sam" />
                <Field label="AzuraCast / self-hosted URL" value={station.azuracast_url} onChange={(v) => upd("azuracast_url", v)} mono testid="field-azuracast" />
                <Field label="Broadcast Radio (Myriad / PlayoutONE) — UK" value={station.broadcast_radio_url} onChange={(v) => upd("broadcast_radio_url", v)} mono testid="field-broadcast-radio" />
              </div>
            </div>

            <div>
              <SectionLabel>Direct HTTPS streams
                <HelpHint title="Stream URL" testid="help-hint-stream-url"
                  text="This is the internet address BroadcastFlow AI uses to retrieve your live radio stream information — song titles, artists, and listener counts."
                  learnMoreSlug="connect-your-stream" />
              </SectionLabel>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Primary HTTPS stream (mp3/aac)" value={station.https_stream_url} onChange={(v) => upd("https_stream_url", v)} mono testid="field-https" />
                <Field label="Backup / fallback stream" value={station.backup_stream_url} onChange={(v) => upd("backup_stream_url", v)} mono testid="field-backup" />
              </div>
            </div>

            <div>
              <SectionLabel>Directories &amp; aggregators</SectionLabel>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="TuneIn" value={station.tunein_url} onChange={(v) => upd("tunein_url", v)} mono testid="field-tunein" />
                <Field label="Live365" value={station.live365_url} onChange={(v) => upd("live365_url", v)} mono testid="field-live365" />
                <Field label="iHeartRadio" value={station.iheartradio_url} onChange={(v) => upd("iheartradio_url", v)} mono testid="field-iheart" />
                <Field label="Audacy / Radio.com" value={station.audacy_url} onChange={(v) => upd("audacy_url", v)} mono testid="field-audacy" />
                <Field label="Mixcloud Live" value={station.mixcloud_url} onChange={(v) => upd("mixcloud_url", v)} mono testid="field-mixcloud" />
                <Field label="Radio Garden" value={station.radiogarden_url} onChange={(v) => upd("radiogarden_url", v)} mono testid="field-radiogarden" />
                <Field label="RadioKing" value={station.radioking_url} onChange={(v) => upd("radioking_url", v)} mono testid="field-radioking" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FutureIntegration label="Spotify" enabled={station.spotify_connected} />
              <FutureIntegration label="Apple Podcasts" enabled={station.apple_podcasts_connected} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="social">
          <div className="space-y-4" data-testid="social-tab">
            <div className="rounded-xl border border-line bg-bg-surface p-6 grid md:grid-cols-2 gap-4">
              <Field label="Facebook" value={station.facebook} onChange={(v) => upd("facebook", v)} testid="field-fb" placeholder="https://facebook.com/yourstation" />
              <Field label="Instagram" value={station.instagram} onChange={(v) => upd("instagram", v)} testid="field-ig" placeholder="https://instagram.com/yourstation" />
              <Field label="X / Twitter" value={station.twitter} onChange={(v) => upd("twitter", v)} testid="field-x" placeholder="https://x.com/yourstation" />
              <Field label="TikTok" value={station.tiktok} onChange={(v) => upd("tiktok", v)} testid="field-tiktok" placeholder="https://tiktok.com/@yourstation" />
              <Field label="YouTube" value={station.youtube} onChange={(v) => upd("youtube", v)} testid="field-yt" placeholder="https://youtube.com/@yourstation" />
              <Field label="LinkedIn" value={station.linkedin} onChange={(v) => upd("linkedin", v)} testid="field-li" placeholder="https://linkedin.com/company/yourstation" />
              <Field label="WhatsApp" value={station.whatsapp} onChange={(v) => upd("whatsapp", v)} testid="field-wa" placeholder="https://wa.me/15551234567" />
              <Field label="PayPal tip link" value={station.paypal_link} onChange={(v) => upd("paypal_link", v)} testid="field-paypal" placeholder="https://paypal.me/yourstation" />
            </div>

            <CustomLinksSection
              links={Array.isArray(station.custom_links) ? station.custom_links : []}
              setLinks={(next) => upd("custom_links", next)}
            />
          </div>
        </TabsContent>

        <TabsContent value="shows">
          <ShowsTab shows={shows} reload={load} />
        </TabsContent>

        <TabsContent value="sponsorships">
          <SponsorshipsTab station={station} upd={upd} />
        </TabsContent>

        <TabsContent value="djs">
          <DJsTab djs={djs} reload={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, value, onChange, mono, testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink-secondary">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`mt-1 bg-bg border-line ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
    </div>
  );
}

// Custom Links — stations can add unlimited extra public links (Bandcamp,
// Discord server, Patreon, merch shop, station phone-in line, etc.). Stored
// on the station doc as `custom_links: [{label, url, icon?}]` so the public
// blog reader, embed widgets, and email signatures can surface them.
function CustomLinksSection({ links, setLinks }) {
  const add = () => setLinks([...(links || []), { label: "", url: "" }]);
  const removeAt = (i) => setLinks(links.filter((_, idx) => idx !== i));
  const updateAt = (i, patch) => setLinks(links.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  return (
    <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-4" data-testid="custom-links-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <SectionLabel>// Custom links</SectionLabel>
          <p className="text-xs text-ink-muted">
            Add any other URL for your station — Discord, Patreon, Bandcamp,
            merch shop, phone-in number, sponsor request form, station press
            kit. These surface on the public blog, the embed widget, and your
            station&apos;s One-Sheet.
          </p>
        </div>
        <button type="button" onClick={add} data-testid="custom-link-add"
                className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-3 py-1.5 text-xs text-brand-amber hover:bg-brand-amber/20 transition shrink-0">
          <Plus className="h-3.5 w-3.5" /> Add link
        </button>
      </div>

      {(!links || links.length === 0) ? (
        <div className="rounded-md border border-dashed border-line bg-bg/40 p-4 text-center text-xs text-ink-muted">
          No custom links yet. Click <span className="text-brand-amber">Add link</span> to surface
          a Patreon, Discord, merch shop, phone-in number or any other URL.
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((lnk, i) => (
            <div key={i} data-testid={`custom-link-row-${i}`}
                 className="grid grid-cols-[1fr_2fr_auto] gap-2 items-center">
              <Input
                value={lnk.label || ""}
                onChange={(e) => updateAt(i, { label: e.target.value })}
                placeholder="Label (e.g. Patreon)"
                className="bg-bg border-line text-sm"
                data-testid={`custom-link-label-${i}`}
              />
              <Input
                value={lnk.url || ""}
                onChange={(e) => updateAt(i, { url: e.target.value })}
                placeholder="https://…"
                className="bg-bg border-line text-sm font-mono"
                data-testid={`custom-link-url-${i}`}
              />
              <button type="button" onClick={() => removeAt(i)} data-testid={`custom-link-remove-${i}`}
                      title="Remove link"
                      className="text-ink-muted hover:text-rose-300 transition p-2">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Note({ children }) {
  return <div className="rounded-md border border-line bg-bg-elevated p-3 text-xs text-ink-secondary flex items-center gap-2">{children}</div>;
}

function SectionLabel({ children }) {
  return (
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80 mb-2">
      // {children}
    </div>
  );
}

function FutureIntegration({ label, enabled }) {
  return (
    <div className="rounded-lg border border-line bg-bg-elevated p-3 flex items-center justify-between">
      <span className="text-sm">{label}</span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{enabled ? "CONNECTED" : "COMING SOON"}</span>
    </div>
  );
}

function ShowsTab({ shows, reload }) {
  const [form, setForm] = useState({ name: "", schedule: "", description: "", genre: "" });
  const [copiedId, setCopiedId] = useState(null);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Show name required"); return; }
    try {
      await api.post("/shows", form);
      toast.success("Show added");
      setForm({ name: "", schedule: "", description: "", genre: "" });
      reload();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    await api.delete(`/shows/${id}`);
    reload();
  };
  const share = (slug) => {
    const url = `${window.location.origin}/s/${slug}`;
    navigator.clipboard.writeText(url);
    setCopiedId(slug);
    toast.success("Public link copied");
    setTimeout(() => setCopiedId(null), 1800);
  };
  const togglePublic = async (s) => {
    try {
      await api.patch(`/shows/${s.show_id}`, { public_enabled: !s.public_enabled });
      reload();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="shows-tab">
      <div className="rounded-xl border border-line bg-bg-surface p-6 grid md:grid-cols-2 gap-3">
        <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="show-name" />
        <Field label="Schedule" value={form.schedule} onChange={(v) => setForm({ ...form, schedule: v })} testid="show-schedule" />
        <Field label="Genre" value={form.genre} onChange={(v) => setForm({ ...form, genre: v })} testid="show-genre" />
        <div className="flex items-end">
          <button onClick={add} data-testid="show-add-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 font-semibold text-black amber-glow w-full justify-center">
            <Plus className="h-4 w-4" /> Add show
          </button>
        </div>
      </div>
      {shows.length === 0 ? (
        <div className="text-sm text-ink-muted py-8 text-center">No shows yet.</div>
      ) : (
        <div className="space-y-2">
          {shows.map((s) => (
            <div key={s.show_id} className="rounded-lg border border-line bg-bg-surface p-4 flex items-center justify-between gap-3" data-testid={`show-${s.show_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Mic2 className="h-4 w-4 text-brand-amber shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-xs text-ink-muted truncate">
                    {s.schedule || "—"} · {s.genre || "—"}
                    {s.slug && <span className="ml-2 font-mono text-brand-amber/70">/s/{s.slug}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => togglePublic(s)}
                  data-testid={`show-toggle-public-${s.show_id}`}
                  title={s.public_enabled ? "Public — click to make private" : "Private — click to publish"}
                  className={`rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition ${
                    s.public_enabled
                      ? "border-brand-amber/40 bg-brand-amber/10 text-brand-amber"
                      : "border-line bg-bg-elevated text-ink-muted"
                  }`}
                >
                  {s.public_enabled ? "PUBLIC" : "PRIVATE"}
                </button>
                {s.slug && s.public_enabled && (
                  <>
                    <button
                      onClick={() => share(s.slug)}
                      data-testid={`show-share-${s.show_id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs hover:text-cyan-300 transition"
                      title="Copy public share link"
                    >
                      {copiedId === s.slug ? <Check className="h-3 w-3 text-brand-amber" /> : <Share2 className="h-3 w-3" />}
                      Share
                    </button>
                    <a
                      href={`/s/${s.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`show-open-${s.show_id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-line bg-bg-elevated px-2 py-1 text-xs hover:text-cyan-300 transition"
                      title="Open public page"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </>
                )}
                <button onClick={() => del(s.show_id)} data-testid={`show-delete-${s.show_id}`} className="text-ink-muted hover:text-brand-onair">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DJsTab({ djs, reload }) {
  const [form, setForm] = useState({ name: "", stage_name: "", email: "", bio: "" });
  const add = async () => {
    if (!form.name.trim()) { toast.error("DJ name required"); return; }
    try {
      await api.post("/djs", form);
      toast.success("DJ added");
      setForm({ name: "", stage_name: "", email: "", bio: "" });
      reload();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/djs/${id}`); reload(); };

  return (
    <div className="space-y-4" data-testid="djs-tab">
      <div className="rounded-xl border border-line bg-bg-surface p-6 grid md:grid-cols-2 gap-3">
        <Field label="Real name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="dj-name" />
        <Field label="Stage name" value={form.stage_name} onChange={(v) => setForm({ ...form, stage_name: v })} testid="dj-stage" />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="dj-email" />
        <div className="flex items-end">
          <button onClick={add} data-testid="dj-add-btn" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 font-semibold text-black amber-glow w-full justify-center">
            <Plus className="h-4 w-4" /> Add DJ
          </button>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-ink-secondary">Bio</Label>
          <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 bg-bg border-line" rows={3} data-testid="dj-bio" />
        </div>
      </div>
      {djs.length === 0 ? (
        <div className="text-sm text-ink-muted py-8 text-center">No DJs on the roster yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {djs.map((d) => (
            <div key={d.dj_id} className="rounded-lg border border-line bg-bg-surface p-4 flex items-center justify-between" data-testid={`dj-${d.dj_id}`}>
              <div>
                <div className="font-display font-bold">{d.stage_name || d.name}</div>
                <div className="text-xs text-ink-muted">{d.name}{d.email ? ` · ${d.email}` : ""}</div>
                {d.bio && <p className="text-xs text-ink-secondary mt-2 line-clamp-2">{d.bio}</p>}
              </div>
              <button onClick={() => del(d.dj_id)} data-testid={`dj-delete-${d.dj_id}`} className="text-ink-muted hover:text-brand-onair shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SponsorshipsTab({ station, upd }) {
  const publicUrl = `${window.location.origin}/sponsor/${station.station_id}`;
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("Public link copied");
    setTimeout(() => setCopied(false), 1500);
  };
  const enabled = !!station.sponsor_briefs_enabled;
  const currency = (station.sponsor_currency || "usd").toLowerCase();

  return (
    <div className="space-y-5" data-testid="sponsorships-tab">
      <div className="rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-lg font-bold mb-1">Open a public sponsor channel</div>
            <p className="text-xs text-ink-muted max-w-xl">
              Turn this on and a hosted page (no auth needed) lets local businesses submit a brief and pay you directly
              via Stripe. Paid briefs land in your <span className="text-brand-amber">Commercials &rarr; Sponsor Inbox</span> ready
              to produce.
            </p>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(v) => upd("sponsor_briefs_enabled", v)}
            data-testid="sponsor-enable-toggle"
          />
        </div>
      </div>

      {enabled && (
        <>
          <div className="rounded-xl border border-line bg-bg-surface p-6">
            <div className="text-xs uppercase tracking-wider text-ink-secondary mb-2">Your public sponsor URL</div>
            <div className="flex items-stretch gap-2">
              <Input value={publicUrl} readOnly className="bg-bg border-line font-mono text-xs flex-1" data-testid="sponsor-public-url" />
              <button onClick={copy} data-testid="sponsor-copy-url" className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-3 text-xs font-semibold text-brand-amber hover:bg-brand-amber/20 transition">
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Copy"}
              </button>
              <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="sponsor-open-url" className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 text-xs text-ink-secondary hover:text-cyan-300 transition">
                <ExternalLink className="h-3.5 w-3.5" /> Open
              </a>
            </div>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-4">
            <div className="font-display text-sm font-bold flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-brand-amber" /> Pricing
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Currency</Label>
                <Input value={currency.toUpperCase()} onChange={(e) => upd("sponsor_currency", e.target.value.toLowerCase())} className="mt-1 bg-bg border-line font-mono text-xs uppercase" data-testid="sponsor-currency" />
              </div>
              <PriceInput label="15s spot" testid="sponsor-price-15" value={station.sponsor_price_15} onChange={(v) => upd("sponsor_price_15", v)} />
              <PriceInput label="30s spot" testid="sponsor-price-30" value={station.sponsor_price_30} onChange={(v) => upd("sponsor_price_30", v)} />
              <PriceInput label="60s spot" testid="sponsor-price-60" value={station.sponsor_price_60} onChange={(v) => upd("sponsor_price_60", v)} />
            </div>
            <p className="text-[11px] text-ink-muted">Tip: leave a price at 0 to hide that length from the public form.</p>
          </div>

          <div className="rounded-xl border border-line bg-bg-surface p-6 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Public blurb</Label>
              <Textarea
                rows={2}
                value={station.sponsor_blurb || ""}
                onChange={(e) => upd("sponsor_blurb", e.target.value)}
                placeholder="e.g. Reach 50,000+ engaged listeners across the Tri-State area every weekday morning."
                className="mt-1 bg-bg border-line"
                data-testid="sponsor-blurb"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-ink-secondary">Terms / delivery promise</Label>
              <Textarea
                rows={2}
                value={station.sponsor_terms || ""}
                onChange={(e) => upd("sponsor_terms", e.target.value)}
                placeholder="e.g. Spots produced within 5 business days. Full refund if your spot isn't approved by the station."
                className="mt-1 bg-bg border-line"
                data-testid="sponsor-terms"
              />
            </div>
            <p className="text-[11px] text-ink-muted">Don&apos;t forget to hit <span className="text-brand-amber">Save</span> at the top of the page to apply changes.</p>
          </div>
        </>
      )}
    </div>
  );
}

function PriceInput({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-ink-secondary">{label}</Label>
      <Input
        type="number"
        min="0"
        step="1"
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        className="mt-1 bg-bg border-line"
        data-testid={testid}
      />
    </div>
  );
}

